import Input from '@orn/shared-web/components/Input';
import PhoneNumberInput, { validatePhoneNumber } from '@orn/shared-web/components/Input/PhoneNumberInput';
import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { CheckCircleIcon, MailIcon, PhoneIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import validateEmail from '@orn/shared-web/lib/validation/emailValidation';
import * as React from 'react';

import { useUpdateDependantContact } from '../../../hooks/mutations/useUpdateDependantContact';
import { useOnboardingDependants } from '../../../hooks/queries/useOnboardingDependants';
import { OnboardingTitle } from '../../OnboardingLayout';

import { getDependantNames, getDisplayName } from './dependantUtils';
import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingDependant } from '../../../hooks/queries/useOnboardingDependants';
import type { IOnboardingStepProps } from '../types';

interface IDependantContactFields {
  email: string;
  phoneNumber: string;
}

const HELPER_ITEMS = [
  {
    icon: <PhoneIcon />,
    label: "We'll use SMS to send important alerts about account changes",
  },
  {
    icon: <MailIcon />,
    label: 'They can forward bills and documents to Mantle via email or SMS for automatic organization',
  },
];

const buildInitialFieldState = (
  dependants: ReadonlyArray<IOnboardingDependant>
): Record<string, IDependantContactFields> => {
  const state: Record<string, IDependantContactFields> = {};
  for (const dependant of dependants) {
    state[dependant.id] = {
      email: dependant.email ?? '',
      phoneNumber: dependant.phoneNumber ?? '',
    };
  }
  return state;
};

/**
 * Per-dependant email + phone capture. Continue fires one
 * `updateDependantContact` mutation per dependant in parallel and only
 * advances once every save lands, so a network failure surfaces before
 * the user moves on. "Skip for now" advances without writing anything.
 *
 * Both fields are optional in the DB (`email` / `phone_number` are
 * nullable on `dependants`), but Continue stays disabled until at least one
 * channel is entered across the dependants — the all-blank path is "Skip for
 * now". Typed-but-invalid values also block Continue. Inputs re-seed from the
 * server on remount, so a returning user sees whatever they previously saved.
 *
 * Figma: Mantle - UX/UI, node 4161:105172 ("Onboarding Step 09").
 */
const DependantContactStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependants, loading } = useOnboardingDependants();
  const [updateDependantContact] = useUpdateDependantContact();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [fields, setFields] = React.useState<Record<string, IDependantContactFields>>(() =>
    buildInitialFieldState(dependants)
  );
  const [touchedIds, setTouchedIds] = React.useState<ReadonlySet<string>>(new Set());

  React.useEffect(() => {
    setFields((prev) => {
      const next = buildInitialFieldState(dependants);
      // Preserve in-memory typing over the freshly-fetched seed.
      for (const id of Object.keys(prev)) {
        if (next[id] !== undefined) {
          next[id] = prev[id];
        }
      }
      return next;
    });
  }, [dependants]);

  const handleChange = React.useCallback(
    (dependantId: string, key: keyof IDependantContactFields) => (value: string) => {
      setTouchedIds((prev) => {
        if (prev.has(dependantId)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(dependantId);
        return next;
      });
      setFields((prev) => ({
        ...prev,
        [dependantId]: {
          ...(prev[dependantId] ?? { email: '', phoneNumber: '' }),
          [key]: value,
        },
      }));
    },
    []
  );

  const validationByDependant = React.useMemo(() => {
    const results: Record<string, { emailError: string; phoneError: string; e164Phone: string | null }> = {};
    for (const dependant of dependants) {
      const entry = fields[dependant.id] ?? { email: '', phoneNumber: '' };
      const trimmedEmail = entry.email.trim();
      const emailValidation = trimmedEmail ? validateEmail(trimmedEmail) : { isValid: true };
      const phoneValidation = entry.phoneNumber
        ? validatePhoneNumber(entry.phoneNumber, false, true)
        : { isValid: true, errorMessage: '', e164Value: null };
      results[dependant.id] = {
        emailError: !emailValidation.isValid && trimmedEmail ? (emailValidation.error ?? '') : '',
        phoneError: phoneValidation.errorMessage,
        e164Phone: phoneValidation.e164Value,
      };
    }
    return results;
  }, [dependants, fields]);

  // At least one contact channel must be entered before Continue is offered —
  // the all-blank path is what "Skip for now" is for.
  const hasAnyContact = React.useMemo(
    () =>
      dependants.some((d) => {
        const entry = fields[d.id] ?? { email: '', phoneNumber: '' };
        return entry.email.trim().length > 0 || entry.phoneNumber.trim().length > 0;
      }),
    [dependants, fields]
  );

  const canSubmit = React.useMemo(() => {
    if (loading || dependants.length === 0 || isSubmitting || !hasAnyContact) {
      return false;
    }
    return dependants.every((d) => {
      const v = validationByDependant[d.id];
      return !v.emailError && !v.phoneError;
    });
  }, [dependants, validationByDependant, loading, isSubmitting, hasAnyContact]);

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Fire one mutation per dependant in parallel. Awaiting all of them
      // keeps the user on this screen until every save lands, so a network
      // failure surfaces before we advance.
      await Promise.all(
        dependants.map((dependant) => {
          const entry = fields[dependant.id] ?? { email: '', phoneNumber: '' };
          const validation = validationByDependant[dependant.id];
          const trimmedEmail = entry.email.trim();
          const normalizedPhone = validation.e164Phone || entry.phoneNumber;
          return updateDependantContact({
            variables: {
              input: {
                dependantId: dependant.id,
                email: trimmedEmail.length > 0 ? trimmedEmail : null,
                phoneNumber: normalizedPhone.length > 0 ? normalizedPhone : null,
              },
            },
          });
        })
      );
      onNext();
    } catch (error) {
      logger.error('Failed to save dependant contact info', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, dependants, fields, validationByDependant, updateDependantContact, onNext]);

  const titleNames = getDependantNames(dependants);
  const successNames = titleNames || 'them';

  return (
    <>
      <OnboardingTitle
        title={titleNames ? `How can we reach ${titleNames}?` : 'How can we reach them?'}
        onBack={onBack}
      />

      <div className="flex w-full flex-col gap-6">
        <MessagingCard variant="success">
          <MessagingCard.Text icon={<CheckCircleIcon />}>
            Mantle will not contact {successNames} until you give explicit permission.
          </MessagingCard.Text>
        </MessagingCard>

        {dependants.map((dependant, index) => {
          const displayName = getDisplayName(dependant);
          const entry = fields[dependant.id] ?? { email: '', phoneNumber: '' };
          const v = validationByDependant[dependant.id];
          const isTouched = touchedIds.has(dependant.id);
          return (
            <React.Fragment key={dependant.id}>
              {index > 0 && <hr className="border-secondary w-full border-t" aria-hidden />}
              <div className="flex w-full flex-col gap-4">
                <Input
                  type="email"
                  label={`${displayName}'s email`}
                  placeholder="parent@email.com"
                  value={entry.email}
                  onChange={handleChange(dependant.id, 'email')}
                  isInvalid={isTouched && Boolean(v.emailError)}
                  hint={isTouched && v.emailError ? v.emailError : undefined}
                />
                <PhoneNumberInput
                  label={`${displayName}'s phone`}
                  placeholder="(555) 000-0000"
                  value={entry.phoneNumber}
                  onChange={handleChange(dependant.id, 'phoneNumber')}
                />
              </div>
            </React.Fragment>
          );
        })}

        <MessagingCard variant="mist">
          <MessagingCard.List items={HELPER_ITEMS} />
        </MessagingCard>
      </div>

      <OnboardingStepActions
        isContinueDisabled={!canSubmit}
        isContinueLoading={isSubmitting}
        onContinue={() => void handleSubmit()}
        onSkip={onNext}
        isSkipDisabled={loading || dependants.length === 0 || isSubmitting}
      />
    </>
  );
};

DependantContactStep.displayName = 'DependantContactStep';

export default DependantContactStep;
